import { ApolloClient } from '@apollo/client/core';
import log from 'loglevel';

import { clearAllStorages, clearIndexedDB } from './resetAppState';
import { closeTerminalConnection } from '../components/Terminal/transport/terminalConnection';
import { useDelegationComposerStore } from '../store/AgentDelegation/DelegationComposer';
import { useAgentDelegationTargetsStore } from '../store/AgentDelegationTargets';
import { useAgentSetupPanelStore } from '../store/AgentSetupPanel';
import { useBrainStore } from '../store/Brain/useBrainStore';
import { useCalendarStore } from '../store/Calendar/useCalendarStore';
import { useCommunicationReportActionsStore } from '../store/CommunicationReportActions';
import { useComposeSnapshotIndexStore } from '../store/ComposeSnapshotIndex';
import { useContactsStore } from '../store/Contacts';
import { useAutoResolvedSuggestionsStore } from '../store/DoBox/useAutoResolvedSuggestionsStore';
import { useDoBoxDropdownStore } from '../store/DoBox/useDoBoxDropdownStore';
import { useDoBoxMultiSelectStore } from '../store/DoBox/useDoBoxMultiSelectStore';
import { useDoBoxPreferencesStore } from '../store/DoBox/useDoBoxPreferencesStore';
import { useDoBoxRulesStore } from '../store/DoBox/useDoBoxRulesStore';
import { useDoBoxSubgroupOpenStore } from '../store/DoBox/useDoBoxSubgroupOpenStore';
import { useOpenDelegationEntryStore } from '../store/DoBox/useOpenDelegationEntryStore';
import { useDoBoxDelegateComposerStore } from '../store/DoBoxDelegateComposer';
import { useDoBoxReplyPanelStore } from '../store/DoBoxReplyPanel';
import { useDoBoxRunViewStore } from '../store/DoBoxRunView';
import { useFetchCacheStore } from '../store/FetchCache/useFetchCacheStore';
import { useGeneratorTransferStore } from '../store/GeneratorTransfer';
import { useInboxAccountFilterStore } from '../store/Inbox/AccountFilter/useInboxAccountFilterStore';
import { useCommunicationIntentsStore } from '../store/Inbox/CommunicationIntents/useCommunicationIntentsStore';
import { useInboxEntriesStore } from '../store/Inbox/InboxEntries/useInboxEntriesStore';
import { useInstagramCommunicationsStore } from '../store/Inbox/InstagramCommunications/useInstagramCommunicationsStore';
import { useIntegrationsStore } from '../store/Inbox/Integrations/useIntegrationsStore';
import { useLabelsStore } from '../store/Inbox/Labels/useLabelsStore';
import { useSelectedItemsStore } from '../store/Inbox/SelectedItems/useSelectedItemsStore';
import { useSlackCommunicationsStore } from '../store/Inbox/SlackCommunications/useSlackCommunicationsStore';
import { useTeamsCommunicationsStore } from '../store/Inbox/TeamsCommunications/useTeamsCommunicationsStore';
import { useUnreadStore } from '../store/Inbox/Unread/useUnreadStore';
import { useUserCommunicationsStore } from '../store/Inbox/UserCommunications/useUserCommunicationsStore';
import { useInboxEntryActivityLogsStore } from '../store/InboxEntryActivityLogs/store';
import { useInboxEntryCommentsStore } from '../store/InboxEntryComments/useInboxEntryCommentsStore';
import { useIntegrationPreferencesStore } from '../store/Integration/useIntegrationPreferencesStore';
import { useMessageDialogStore } from '../store/MessageDialog/useMessageDialogStore';
import { useNotificationsStore } from '../store/Notifications/useNotificationsStore';
import { useOnboardingStore } from '../store/Onboarding/useOnboardingStore';
import { usePendingActionsStore } from '../store/PendingActions/usePendingActionsStore';
import { usePlatformStore } from '../store/Platform/usePlatformStore';
import { useSentChatRepliesStore } from '../store/SentChatReplies';
import { useSuggestionTabAlertsStore } from '../store/SuggestionTabAlerts/useSuggestionTabAlertsStore';
import { useTeamStore } from '../store/Team/useTeamStore';
import { useTerminalStore } from '../store/Terminal/useTerminalStore';

const PERSISTED_STORES = [
  useBrainStore,
  useIntegrationsStore,
  useOnboardingStore,
  useInboxEntriesStore,
  useUserCommunicationsStore,
  useCommunicationIntentsStore,
  useSlackCommunicationsStore,
  useTeamsCommunicationsStore,
  useInstagramCommunicationsStore,
  useSelectedItemsStore,
  useLabelsStore,
  useDoBoxDropdownStore,
  useDoBoxPreferencesStore,
  usePlatformStore,
  useIntegrationPreferencesStore,
  useTeamStore,
  useMessageDialogStore,
  useDelegationComposerStore,
] as const;

const clearPersistedStores = async (): Promise<void> => {
  await Promise.all(
    PERSISTED_STORES.map(async (store) => {
      try {
        await Promise.resolve(store.persist.clearStorage());
      } catch (error) {
        log.error('Failed to clear persisted store storage', { error });
      }
    }),
  );
};

const resetInMemoryStores = () => {
  useBrainStore.getState().resetBrain();
  useIntegrationsStore.getState().resetStore();
  useOnboardingStore.getState().resetOnboarding();
  useInboxEntriesStore.getState().resetState();
  useUserCommunicationsStore.getState().resetState();
  useCommunicationIntentsStore.getState().resetState();
  useSlackCommunicationsStore.getState().resetState();
  useTeamsCommunicationsStore.getState().resetState();
  useInstagramCommunicationsStore.getState().resetState();
  usePlatformStore.getState().resetState();
  useSelectedItemsStore.getState().resetState();
  useLabelsStore.getState().resetState();
  useDoBoxDropdownStore.getState().resetState();
  useDoBoxPreferencesStore.getState().reset();
  useIntegrationPreferencesStore.getState().reset();
  useDoBoxRulesStore.getState().resetState();
  useDoBoxSubgroupOpenStore.getState().reset();
  useOpenDelegationEntryStore.getState().reset();
  useDoBoxReplyPanelStore.getState().reset();
  useDoBoxDelegateComposerStore.getState().reset();
  useDoBoxRunViewStore.getState().reset();
  useSentChatRepliesStore.getState().reset();
  useComposeSnapshotIndexStore.getState().resetState();
  useAgentSetupPanelStore.getState().reset();
  useFetchCacheStore.getState().clearCache();
  useTeamStore.getState().resetState();
  useDelegationComposerStore.getState().resetState();
  useAgentDelegationTargetsStore.getState().resetState();

  useCalendarStore.getState().resetState();
  closeTerminalConnection();
  useTerminalStore.getState().reset();
  useCommunicationReportActionsStore.getState().resetState();
  useContactsStore.getState().reset();
  useDoBoxMultiSelectStore.getState().clear();
  useInboxAccountFilterStore.getState().reset();
  useUnreadStore.getState().resetState();
  useInboxEntryActivityLogsStore.getState().resetState();
  useInboxEntryCommentsStore.getState().resetState();
  useNotificationsStore.getState().clearNotifications();
  usePendingActionsStore.getState().clearAllPendingActions();
  useGeneratorTransferStore.getState().clear();
  useSuggestionTabAlertsStore.getState().reset();
  useAutoResolvedSuggestionsStore.getState().reset(null);

  // useMessageDialogStore — UI-only open/close state, safe to leave across accounts;
  // dialogs flush through their own registered close handler, and a hard reset here would
  // drop whatever save is in flight instead of letting it complete
  // useFloatingAssistantStore — open/peek state is safe to leave across accounts
  // useDoBoxTaskGroupSelectorStore — UI-only panel open flag, safe to leave across accounts
  // useGTMStore — analytics event-fired flags; cleared via clearAllStorages (localStorage/sessionStorage)
};

export const clearAllUserState = async (
  apolloClient?: ApolloClient,
): Promise<void> => {
  resetInMemoryStores();

  await clearPersistedStores();

  await clearIndexedDB();

  if (apolloClient) {
    await apolloClient.clearStore();
  }

  clearAllStorages();
};
